import { useEffect, useRef, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PaymentMethodChooser } from './PaymentMethodChooser';
import { CashPanel } from './CashPanel';
import { EmoneyPanel } from './EmoneyPanel';
import { MidtransQrisPanel } from './MidtransQrisPanel';
import { EdcPanel } from './EdcPanel';
import { usePaymentMethods } from '@/hooks/queries';
import { useCreateSale } from '@/hooks/mutations';
import { useOnline } from '@/hooks/useOnline';
import { buildSalePayload } from '@/lib/buildSalePayload';
import { useCartStore } from '@/stores/cart.store';
import { useDeviceStore } from '@/stores/device.store';
import type { PaymentMethod } from '@/types/payment';
import type { CreateSaleResult, SaleStatus, SocketConfig } from '@/types/sale';
import type { ApiError } from '@/types/api';

type Stage = 'choose' | 'cash' | 'emoney' | 'qris' | 'edc';

interface PaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired when a sale reaches SUCCESS — hands the invoiceId to the print flow. */
  onPaid: (result: CreateSaleResult) => void;
  /** Capacity 409 — bounce back to the date/slot picker. */
  onCapacityBlocked?: () => void;
  /**
   * Authoritative amount due (the BE-quoted total, incl. discounts + rate plan).
   * Falls back to the client estimate when the quote is unavailable. Used for
   * the cash tendered/change math so it matches the eventual charge.
   */
  total: number;
}

export function PaymentDialog({
  open,
  onOpenChange,
  onPaid,
  onCapacityBlocked,
  total,
}: PaymentDialogProps) {
  const [stage, setStage] = useState<Stage>('choose');
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [socketConfig, setSocketConfig] = useState<SocketConfig | null>(null);
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [qrString, setQrString] = useState<string | null>(null);
  // The server-resolved QR image. Only ASPI needs it: an ASPI border exists only
  // in Midtrans' rendered PNG, so a QR we draw ourselves cannot carry one.
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [paymentExpire, setPaymentExpire] = useState<string | null>(null);
  const [qrisStatus, setQrisStatus] = useState<SaleStatus>('PENDING');

  const { data: methods = [] } = usePaymentMethods();
  const reader = useDeviceStore((s) => s.reader);
  const { mutate: createSale, isPending } = useCreateSale();
  const online = useOnline();
  // One idempotency key per checkout session — reused across retries of the
  // same cart so a flaky-network retry can't double-create the sale.
  const idemKeyRef = useRef<string>('');

  // Reset on (re)open.
  useEffect(() => {
    if (open) {
      setStage('choose');
      setMethod(null);
      setSocketConfig(null);
      setInvoiceId(null);
      setQrString(null);
      setQrImageUrl(null);
      setPaymentExpire(null);
      setQrisStatus('PENDING');
      idemKeyRef.current = crypto.randomUUID();
    }
  }, [open]);

  /** Create the sale for the chosen method. `amountTendered` only for cash. */
  function submitFor(m: PaymentMethod, amountTendered?: number) {
    // Never fire a payment while offline — the sale reserves capacity + settles
    // server-side, so a queued submit could land in a sold-out slot or a failed
    // charge after the customer has left. Block clearly instead.
    if (!online) {
      toast.error('Koneksi terputus — transaksi tidak dapat diproses. Sambungkan kembali internet.');
      return;
    }
    const cartState = useCartStore.getState();
    const payload = buildSalePayload({
      lines: cartState.lines,
      bundleLines: cartState.bundleLines,
      customer: cartState.customer,
      visitDate: cartState.visitDate,
      couponCode: cartState.couponCode,
      discountIds: cartState.discountIds,
      paymentMethodId: m.id,
      paymentGateway: m.paymentGateway,
      amountTendered,
      reader: m.paymentGateway === 'e-money' ? reader : undefined,
      groupType: cartState.promo.groupType,
      sourceChannel: cartState.promo.sourceChannel,
      identity: cartState.promo.identity,
      documentScanToken: cartState.promo.documentScanToken,
    });

    createSale({ payload, idempotencyKey: idemKeyRef.current }, {
      onSuccess: (result) => {
        setInvoiceId(result.invoiceId);
        if (m.paymentGateway === 'cash') {
          onPaid(result);
        } else if (m.paymentGateway === 'e-money') {
          if (result.socket) setSocketConfig(result.socket);
          else toast.error('Server tidak mengirim konfigurasi pembaca kartu.');
        } else if (m.paymentGateway === 'edc-permata') {
          // `socket.room` IS the PaymentTransaction id — the handle needed to
          // resolve the payment later if the terminal never answers.
          if (result.socket) setSocketConfig(result.socket);
          else toast.error('Server tidak mengirim status mesin EDC. Cek mesin sebelum mengulang.');
        } else {
          // `qr_string` is what a QRIS charge returns; `token` is the Snap
          // shape. Reading only the latter two left a QRIS sale with no
          // payload at all, so the panel's fallback had nothing to draw and
          // sat on a spinner instead of a code.
          setQrString(
            result.meta?.qr_string ?? result.meta?.qrString ?? result.meta?.token ?? null,
          );
          setQrImageUrl(result.qris?.format === 'ASPI' ? (result.qris.url ?? null) : null);
          setPaymentExpire(result.paymentExpire ?? null);
          // QRIS now gets a channel too, so the cashier sees the payment land
          // instead of waiting for the next poll. Its absence is survivable —
          // the panel keeps polling — so unlike e-money and EDC this does not
          // warn the cashier.
          if (result.socket) setSocketConfig(result.socket);
          setQrisStatus(result.status ?? 'PENDING');
        }
      },
      onError: (err) => {
        const apiErr = err as ApiError;
        const blocked =
          apiErr?.code === 409 || (apiErr?.payload as { blocked?: boolean })?.blocked === true;
        toast.error(apiErr?.message ?? 'Gagal membuat transaksi.');
        if (m.paymentGateway !== 'cash') setStage('choose');
        if (blocked && onCapacityBlocked) {
          onOpenChange(false);
          onCapacityBlocked();
        }
      },
    });
  }

  function choose(m: PaymentMethod) {
    setMethod(m);
    if (m.paymentGateway === 'cash') {
      setStage('cash');
    } else if (m.paymentGateway === 'e-money') {
      setStage('emoney');
      submitFor(m); // immediately create sale to obtain the socket config
    } else if (m.paymentGateway === 'edc-permata') {
      setStage('edc');
      submitFor(m); // the sale is committed BEFORE the terminal is asked
    } else {
      setStage('qris');
      submitFor(m);
    }
  }

  /**
   * Return to the chooser AND mint a new idempotency key, so the next attempt
   * creates a real new sale instead of replaying the cached response of the
   * last one.
   *
   * Only ever called where the SERVER said it is safe (`retrySafe`) — i.e.
   * something authoritative stated no money moved: the gateway's own word for
   * midtrans, `edc.state` for EDC. It is never true for e-money, which cannot
   * tell an unreachable reader from a declined tap. The client does not make
   * that judgment; it only acts on it.
   */
  function restartWithFreshKey() {
    idemKeyRef.current = crypto.randomUUID();
    backToChoose();
  }

  function backToChoose() {
    setStage('choose');
    setMethod(null);
    setSocketConfig(null);
    setQrString(null);
    setQrImageUrl(null);
    setPaymentExpire(null);
    setQrisStatus('PENDING');
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" hideClose={stage !== 'choose'}>
        <DialogHeader>
          <DialogTitle>
            {stage === 'choose'
              ? 'Pilih metode pembayaran'
              : stage === 'cash'
                ? 'Pembayaran tunai'
                : stage === 'emoney'
                  ? 'Pembayaran e-money'
                  : stage === 'edc'
                    ? 'Pembayaran kartu (EDC)'
                    : 'Pembayaran QRIS'}
          </DialogTitle>
          <DialogDescription>
            {stage === 'choose'
              ? 'Pilih bagaimana pelanggan akan membayar.'
              : 'Selesaikan pembayaran untuk mencetak tiket.'}
          </DialogDescription>
        </DialogHeader>

        {!online && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive">
            <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
            Koneksi terputus — pembayaran ditunda sampai internet tersambung kembali.
          </div>
        )}

        {stage === 'choose' && (
          <PaymentMethodChooser methods={methods} selectedId={method?.id ?? null} onSelect={choose} />
        )}

        {stage === 'cash' && (
          <CashPanel
            total={total}
            isSubmitting={isPending}
            onConfirm={(amt) => method && submitFor(method, amt)}
          />
        )}

        {stage === 'emoney' && (
          <EmoneyPanel
            total={total}
            socketConfig={socketConfig}
            onSuccess={() => {
              if (invoiceId) onPaid({ invoiceId, status: 'SUCCESS' });
            }}
            onCancel={backToChoose}
            onRetry={() => method && submitFor(method)}
          />
        )}

        {stage === 'edc' && (
          <EdcPanel
            total={total}
            socketConfig={socketConfig}
            paymentTransactionId={socketConfig?.room ?? null}
            onSuccess={() => invoiceId && onPaid({ invoiceId, status: 'SUCCESS' })}
            onCancel={() => {
              backToChoose();
              onOpenChange(false);
            }}
            // A fresh key ONLY where the server proved no money moved
            // (DECLINED / FAILED_NOT_SENT). An UNRESOLVED terminal never
            // reaches here, but if it somehow did, this falls back to the
            // replay rather than risking a second charge.
            onRetry={(retrySafe) => (retrySafe ? restartWithFreshKey() : backToChoose())}
          />
        )}

        {stage === 'qris' && invoiceId && (
          <MidtransQrisPanel
            total={total}
            invoiceId={invoiceId}
            qrString={qrString}
            qrImageUrl={qrImageUrl}
            paymentExpire={paymentExpire}
            socketConfig={socketConfig}
            status={qrisStatus}
            onStatus={setQrisStatus}
            onSuccess={() => onPaid({ invoiceId, status: 'SUCCESS' })}
            onCancel={backToChoose}
            onRestart={restartWithFreshKey}
          />
        )}

        {stage === 'cash' && (
          <div className="flex justify-start">
            <Button variant="ghost" size="sm" onClick={backToChoose}>
              ← Ganti metode
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
